import { useMemo } from 'react';
import type { FC, ReactElement, ReactNode } from 'react';
import { Flex, Progress } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { GetIcon } from '../../../components/icons';
import { useCart } from '../../../hooks/useCart';
import { SHIPS_FREE_LIST } from '../../../hooks/useIsFreeShipping';
import { Text } from '../../../ui-components';
import { useCustomPreferences } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';
import {
    hasEligibleItemsForFreeShipping,
    isEligibleItemForFreeShipping,
} from '../../../utils/product-utils';
import { classNames } from '../../../utils/tailwind/classNames';
import { FreeShippingBarSkeleton } from './CartSkeleton';

interface ProgressBarContentProps {
    id: string;
    maxWidth?: string;
    className?: string;
}

export const FreeShippingBar: FC<ProgressBarContentProps> = ({ id, className, maxWidth }) => {
    const { basket, shipToShippingTotal } = useCart();

    const customPreferenceData = useCustomPreferences([
        CustomPreference.ENABLE_THRESHOLD_SHIPPING,
        CustomPreference.THRESHOLD_SHIPPING_AMOUNT,
    ]);

    const enableThresholdShipping =
        customPreferenceData[CustomPreference.ENABLE_THRESHOLD_SHIPPING] ?? false;
    const thresholdShippingAmount =
        (customPreferenceData[CustomPreference.THRESHOLD_SHIPPING_AMOUNT] as number) ?? 75;

    const { amountNeededForFreeShipping, percentageCompleted, onlyShipsFreeItems } = useMemo(() => {
        const productItems = basket?.productItems ?? [];

        const { freeShippingAmount, shipsFreeCount, nonShipsFreeCount } = productItems.reduce(
            (acc, productItem) => {
                const { c_promotions, price, priceAfterItemDiscount, priceAfterOrderDiscount } =
                    productItem;

                const isShipsFree = c_promotions?.some(({ promotionId }) =>
                    SHIPS_FREE_LIST.some((shipsFree) =>
                        promotionId.toLowerCase().includes(shipsFree),
                    ),
                );

                if (!isShipsFree && isEligibleItemForFreeShipping(productItem, productItems)) {
                    acc.freeShippingAmount +=
                        !Number.isNaN(price) && price !== undefined
                            ? Math.min(
                                  priceAfterItemDiscount ?? price,
                                  priceAfterOrderDiscount ?? price,
                                  price,
                              )
                            : 0;
                }

                if (isShipsFree) {
                    acc.shipsFreeCount += 1;
                } else {
                    acc.nonShipsFreeCount += 1;
                }

                return acc;
            },
            { freeShippingAmount: 0, shipsFreeCount: 0, nonShipsFreeCount: 0 },
        );

        const calculatedAmountNeededForFreeShipping = Math.max(
            0,
            thresholdShippingAmount - freeShippingAmount,
        ).toFixed(2);

        const calculatedPercentageCompleted = Math.min(
            100,
            (freeShippingAmount / thresholdShippingAmount) * 100,
        );

        const _onlyShipsFreeItems = nonShipsFreeCount === 0 && shipsFreeCount > 0;

        if (shipToShippingTotal === 0) {
            return {
                amountNeededForFreeShipping: 0,
                percentageCompleted: 100,
                onlyShipsFreeItems: _onlyShipsFreeItems,
            };
        }

        return {
            amountNeededForFreeShipping: calculatedAmountNeededForFreeShipping,
            percentageCompleted: calculatedPercentageCompleted,
            onlyShipsFreeItems: _onlyShipsFreeItems,
        };
    }, [thresholdShippingAmount, basket?.productItems, shipToShippingTotal]);

    const isFreeShippingBarEnabled = hasEligibleItemsForFreeShipping(basket?.productItems);

    if (
        !isFreeShippingBarEnabled ||
        !enableThresholdShipping ||
        !basket?.productItems?.length ||
        onlyShipsFreeItems
    ) {
        return null;
    }

    return (
        <div
            data-testid={id}
            className={classNames(
                'flex w-full justify-center sm:justify-start md:rounded-2xl md:bg-white md:p-4 md:shadow-slt-cart-box',
                className,
            )}
        >
            <Flex
                direction="column"
                width="100%"
                gap={2}
                maxWidth={maxWidth}
                justify="center"
                data-testid="cart-free-shipping-bar"
            >
                <Flex width="100%" align="center" gap={1}>
                    <GetIcon name="progress-bar" size="lg" />
                    <Flex grow={2}>
                        <Progress
                            value={percentageCompleted}
                            height="16px"
                            size="sm"
                            width="100%"
                            borderRadius="10px"
                            padding="4px"
                            variant="cart"
                        />
                    </Flex>
                    <Text size="small">{`$${
                        customPreferenceData[CustomPreference.THRESHOLD_SHIPPING_AMOUNT]
                    }.00`}</Text>
                </Flex>
                <Flex justify="center">
                    <Text size="small">
                        {percentageCompleted < 100 ? (
                            <FormattedMessage
                                id="cart.free-shipping.below-threshold"
                                // eslint-disable-next-line no-template-curly-in-string
                                defaultMessage="You’re only <bold>${thresholdDifference}</bold> away to get free shipping!"
                                values={{
                                    thresholdDifference: amountNeededForFreeShipping,
                                    bold: BoldText,
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="cart.free-shipping.qualifies-for-free-shipping"
                                defaultMessage="Great! Your order qualifies for <bold>free shipping!</bold>"
                                values={{
                                    bold: BoldText,
                                }}
                            />
                        )}
                    </Text>
                </Flex>
            </Flex>
        </div>
    );
};

FreeShippingBar.defaultProps = {
    maxWidth: undefined,
    className: undefined,
};

const BoldText = (chunks: ReactNode[]): ReactElement => (
    <Text size="small" style="bold">
        {chunks}
    </Text>
);

type FreeShippingBarWrapperProps = {
    id: string;
    isCartLoading: boolean;
    maxWidth?: string;
    className?: string;
};

export const FreeShippingBarWrapper: FC<FreeShippingBarWrapperProps> = ({
    id,
    isCartLoading,
    maxWidth,
    className,
}) => {
    if (isCartLoading) {
        return <FreeShippingBarSkeleton />;
    }

    return <FreeShippingBar id={id} maxWidth={maxWidth} className={className} />;
};

FreeShippingBarWrapper.defaultProps = {
    maxWidth: undefined,
    className: undefined,
};
